import React, { useState } from "react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { RefreshCw } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAuth } from "@/hooks/useAuth"
import { councilDashboardApi } from "@/lib/api"

interface PlatformCombinationData {
  name: string
  value: number
}

interface PlatformCombinationsProps {
  title?: string
  councilId?: string | undefined
}

// CSV strings: each entry is one "group" param value
const PLATFORM_GROUPS: string[] = ["IOS,ANDROID", "IOS,WEB", "ANDROID,WEB", "IOS,ANDROID,WEB"]

export function PlatformCombinations({
  title = "Platform Combinations",
  councilId: propsCouncilId,
}: PlatformCombinationsProps) {
  const { user } = useAuth()
  const councilId = propsCouncilId || user?.councils?.[0]?.id?.toString()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [combinations, setCombinations] = useState<PlatformCombinationData[]>([])

  React.useEffect(() => {
    if (!councilId) return

    const fetchData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await councilDashboardApi.getUserCountByPlatformGroups(
          Number(councilId),
          PLATFORM_GROUPS,
        )
        const data = (response.data.data ?? [])
          .filter((item) => (item.count ?? 0) > 0)
          .map((item) => ({
            name: (item.platforms ?? []).join(", "),
            value: item.count ?? 0,
          }))
          .sort((a, b) => b.value - a.value)

        setCombinations(data)
      } catch (err) {
        console.error("Failed to fetch platform combinations:", err)
        setError("Failed to load platform data")
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [councilId])

  if (!councilId) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{title}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Select a council to view platform data
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Council ID required
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{title}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Loading platform data...</p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="size-8 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{title}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1 text-destructive">{error}</p>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{title}</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">Platform combination distribution</p>
      </CardHeader>
      <CardContent>
        {combinations.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            No platform combination data available
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={combinations} margin={{ top: 10, right: 10, left: -20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  stroke="#6b7280"
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke="#6b7280" allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="value" fill="#3b82f6" name="Users" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
